import { useEffect, useState, type ReactNode } from "react";

import {
  DISH_UNITS,
  DISH_UNIT_G,
  EMPTY_DISH,
  dishDraftOf,
  dishItemOf,
  dishPer100,
  dishSums,
  portionWords,
  potHolds,
  readDish,
  typedNumber,
  wentInKcal,
  type DishDraft,
  type DishUnit,
} from "../domain/dish";
import { NUTRIENT_WORD } from "../domain/foodView";
import type { CustomFood } from "../domain/types";
import { loadFoods } from "../lib/foodDb";
import { useKaya } from "../store/kayaStore";
import { addCustomFood, editCustomFood, removeCustomFood } from "../store/toggles";
import { ActionBar } from "./ActionBar";
import { IconButton } from "./Button";
import { ConfirmSheet } from "./ConfirmSheet";
import { ChoiceField, Field, FieldGrid } from "./Fields";
import { FoodPicker } from "./FoodPicker";
import { I } from "./icons";
import { NavRow, RowGroup } from "./Rows";
import { Segmented } from "./Segmented";
import { Sheet } from "./Sheet";
import { Tag } from "./Tag";
import "./FoodAdd.css";

const n = (v: number) => v.toLocaleString();
const one = (v: number) => (Math.round(v * 10) / 10).toLocaleString();
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

type Key = "k" | "p" | "c" | "f" | "fb" | "sf" | "na" | "ca" | "fe" | "pot";
const ROWS: { key: Key; label: string; unit: "kcal" | "g" | "mg"; dp: 0 | 1 }[] = [
  { key: "k", label: "Energy", unit: "kcal", dp: 0 },
  { key: "p", label: "Protein", unit: "g", dp: 1 },
  { key: "c", label: "Carbohydrate", unit: "g", dp: 1 },
  { key: "f", label: "Fat", unit: "g", dp: 1 },
  { key: "fb", label: "Fibre", unit: "g", dp: 1 },
  { key: "sf", label: "Saturated fat", unit: "g", dp: 1 },
  { key: "na", label: "Sodium", unit: "mg", dp: 0 },
  { key: "ca", label: "Calcium", unit: "mg", dp: 0 },
  { key: "fe", label: "Iron", unit: "mg", dp: 1 },
  { key: "pot", label: "Potassium", unit: "mg", dp: 0 },
];

const cell = (v: number | undefined, row: (typeof ROWS)[number]): string => {
  if (typeof v !== "number") return "–";
  const fig = row.dp ? one(v) : n(Math.round(v));
  return row.key === "k" ? `~${fig} kcal` : `${fig} ${row.unit}`;
};

function andList(xs: string[]): string {
  return xs.length <= 1 ? (xs[0] ?? "") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`;
}

/**
 * "A dish I cook" (board FoodBatchDish; ADR-048): what went into the pot, searched from the table
 * in grams, katori, spoons or pieces; the cooked pot's weight; the portion it is eaten by; and the
 * values worked out from the pot, shown before saving with every nutrient an entry will copy.
 *
 * Saved as a food of the user's own through `store/toggles.ts`. An edit is a new version — a
 * tombstone plus a new id (ADR-026) — so a katori already logged keeps its figures.
 */
export function DishForm({
  initial,
  prefillName,
  kindSwitch,
  onClose,
  onSaved,
}: {
  initial: CustomFood | null;
  prefillName?: string;
  /** the label/dish choice, shown above the name on a new food */
  kindSwitch?: ReactNode;
  onClose: () => void;
  onSaved?: (id: string) => void;
}) {
  const { update, showToast } = useKaya();
  const [d, setD] = useState<DishDraft>(
    () => (initial && dishDraftOf(initial)) || { ...EMPTY_DISH, name: prefillName?.trim() ?? "" }
  );
  const [picking, setPicking] = useState(false);
  const [asking, setAsking] = useState(false);
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    let live = true;
    loadFoods()
      .then((f) => live && setCount(f.length))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  const set = <K extends keyof DishDraft>(k: K, v: DishDraft[K]) => setD((x) => ({ ...x, [k]: v }));
  const read = readDish(d);
  const unchanged =
    initial !== null &&
    read.food !== undefined &&
    JSON.stringify(read.food) ===
      JSON.stringify({ name: initial.name, unit: initial.unit, g: initial.g, per100: initial.per100, dish: initial.dish });
  const reason = read.reason ?? (unchanged ? "No changes yet" : undefined);

  const sums = dishSums(d.items);
  const pot = typedNumber(d.pot);
  const g = typedNumber(d.g);
  const per = dishPer100(d.items, pot);
  const holds = potHolds(pot, g);
  const wentIn = wentInKcal(d.items, g);
  const perPortionK = per && g > 0 ? Math.round((per.k * g) / 100) : null;
  const lackingKeys = (Object.keys(sums.lacking) as (keyof typeof sums.lacking)[]).filter((k) => sums.lacking[k]?.length);
  const lackingNames = [...new Set(lackingKeys.flatMap((k) => sums.lacking[k] ?? []))];

  const save = () => {
    const food = read.food;
    if (!food) return;
    const out: { id: string | null } = { id: null };
    update((s) => {
      out.id = initial ? editCustomFood(s, initial.id, food) : addCustomFood(s, food);
    });
    if (out.id === null) {
      showToast("That dish has changed elsewhere");
      onClose();
      return;
    }
    showToast("Saved");
    onSaved?.(out.id);
    onClose();
  };

  const remove = () => {
    if (!initial) return;
    update((s) => removeCustomFood(s, initial.id));
    showToast(`Deleted ${initial.name}`);
    setAsking(false);
    onClose();
  };

  if (asking && initial) {
    return (
      <ConfirmSheet
        title={`Delete ${initial.name}?`}
        sub="What you already logged from it stays as it is."
        confirm={{ label: "Delete dish", onClick: remove }}
        onClose={() => setAsking(false)}
      />
    );
  }

  return (
    <>
      <Sheet
        title={initial ? "Edit dish" : "Add a food"}
        sub={initial ? "Changes apply to what you log from now on" : "Kept with your account, on every phone you sign in on"}
        onClose={onClose}
        footer={
          <ActionBar
            inSheet
            secondary={{ label: "Cancel", onClick: onClose }}
            primary={{ label: "Save dish", icon: I.check, onClick: save, disabled: reason !== undefined, reason }}
          />
        }
      >
        <div className="k-fields">
          {kindSwitch}
          <Field label="Name" type="text" value={d.name} placeholder="My rajma" onChange={(v) => set("name", v)} autoComplete="off" />

          <section className="k-dish" aria-label="What went in">
            <h3 className="k-dish__h">What went in</h3>
            <p className="k-food-note">Everything as it went into the pot, raw or boiled as you used it.</p>
            <RowGroup>
              {d.items.map((it, i) => (
                <NavRow
                  key={`${i}:${it.code ?? it.name}`}
                  kind="action"
                  title={it.name}
                  subtitle={`${portionWords(it.qty, it.unit)} · ${n(it.grams)} g`}
                  action={
                    <span className="k-food-acts">
                      <span className="k-food-kcal">{n(it.kcal)} kcal</span>
                      <IconButton
                        icon={I.close}
                        label={`Remove ${it.name} from the dish`}
                        onClick={() => set("items", d.items.filter((_, j) => j !== i))}
                      />
                    </span>
                  }
                />
              ))}
              <NavRow
                icon={I.plus}
                tone="brand"
                title="Add an ingredient"
                subtitle={`Search ${count ? `${n(count)} foods` : "foods"} · grams, katori, spoons or pieces`}
                onClick={() => setPicking(true)}
              />
            </RowGroup>
            {d.items.length ? (
              <p className="k-dish__sum">
                {n(sums.grams)} g went in · {n(sums.kcal)} kcal
              </p>
            ) : null}
          </section>

          <Field
            label="The cooked pot weighs"
            type="number"
            unit="g"
            min="0"
            step="1"
            value={d.pot}
            onChange={(v) => set("pot", v)}
            hint="Weigh the pot with the dish, then take off the empty pot. Water added or boiled off is why it differs from what went in."
          />

          <ChoiceField label="You eat it by the">
            <Segmented<DishUnit>
              ariaLabel="You eat it by the"
              value={d.unit}
              onChange={(u) => setD((x) => ({ ...x, unit: u, g: String(DISH_UNIT_G[u]) }))}
              options={DISH_UNITS.map((u) => ({ value: u, label: cap(u) }))}
            />
          </ChoiceField>

          <FieldGrid>
            <Field label={`One ${d.unit} weighs`} type="number" unit="g" min="0" step="1" value={d.g} onChange={(v) => set("g", v)} />
            <div className="k-dish__holds">
              {holds !== null ? (
                <p className="k-dish__sum">
                  The pot holds {n(holds)} {d.unit}.
                </p>
              ) : null}
            </div>
          </FieldGrid>

          {per && g > 0 ? (
            <section className="k-dish__panel" aria-label="Worked out from the pot">
              <div className="k-dish__panelh">
                <h3 className="k-dish__h">Worked out from the pot</h3>
                <Tag tone="est" />
              </div>
              <div className="k-dish__table" role="table" aria-label={`${d.name || "The dish"} per 100 g and per ${d.unit}`}>
                <span className="k-dish__th" role="columnheader" />
                <span className="k-dish__th" role="columnheader">
                  Per 100 g
                </span>
                <span className="k-dish__th" role="columnheader">
                  Per {d.unit} · {n(g)} g
                </span>
                {ROWS.map((row) => {
                  const v100 = per[row.key];
                  const vPortion = typeof v100 === "number" ? (v100 * g) / 100 : undefined;
                  return [
                    <span key={`${row.key}h`} className="k-dish__rh" role="rowheader">
                      {row.label}
                    </span>,
                    <span key={`${row.key}a`} className={typeof v100 === "number" ? "" : "k-dish__na"} role="cell">
                      {cell(v100, row)}
                    </span>,
                    <span key={`${row.key}b`} className={typeof vPortion === "number" ? "" : "k-dish__na"} role="cell">
                      {cell(vPortion, row)}
                    </span>,
                  ];
                })}
              </div>
              <p className="k-food-note">
                {n(sums.kcal)} kcal in the pot ÷ {n(pot)} g.
                {wentIn !== null && perPortionK !== null && Math.abs(wentIn - perPortionK) >= 1
                  ? ` Divided by the ${n(sums.grams)} g that went in, a ${d.unit} would read ~${n(wentIn)} kcal; the water is the difference.`
                  : ""}
              </p>
              {lackingNames.length ? (
                <p className="k-food-note">
                  {andList(lackingNames)} {lackingNames.length === 1 ? "has" : "have"} no{" "}
                  {andList(lackingKeys.map((k) => NUTRIENT_WORD[k]))} on record, so the dish shows none rather than too
                  little.
                </p>
              ) : null}
            </section>
          ) : null}

          <p className="k-food-note">
            Saved as yours. Change it later and only new entries follow; what you already logged keeps its figures.
          </p>

          {initial ? (
            <RowGroup>
              <NavRow kind="danger" icon={I.trash} title="Delete this dish" onClick={() => setAsking(true)} />
            </RowGroup>
          ) : null}
        </div>
      </Sheet>
      {picking ? (
        <FoodPicker
          mode="ingredient"
          title="Add an ingredient"
          action="Add"
          onClose={() => setPicking(false)}
          onPick={(e) => {
            setD((x) => ({ ...x, items: [...x.items, dishItemOf(e)] }));
            setPicking(false);
          }}
        />
      ) : null}
    </>
  );
}
